import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Phone, MessageCircle, Send, CalendarPlus, Pencil, Trash2, Plus, ShieldCheck, Download } from "lucide-react";
import { downloadJson } from "@/components/AccountPrivacy";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { INTERACTION_TYPES, productOf, stageOf, statusLabel, fmtEur, fmtDate, fmtDateTime, telLink, waLink } from "@/lib/crm";
import { ClientFormDialog } from "@/components/ClientFormDialog";
import { PracticeFormDialog } from "@/components/PracticeFormDialog";
import { ReminderDialog } from "@/components/ReminderDialog";
import { CompleteReminderDialog } from "@/components/CompleteReminderDialog";
import { MessageDialog } from "@/components/MessageDialog";
import { ReminderRow } from "@/components/ReminderRow";
import { AiSummary } from "@/components/ai/AiSummary";
import { PrivacyCard } from "@/components/PrivacyCard";

const Info = ({ label, value, mono }) => (
  <div><p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p><p className={`text-sm text-slate-800 mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</p></div>
);

const LogForm = ({ clientId }) => {
  const [type, setType] = useState("chiamata");
  const [content, setContent] = useState("");
  const refresh = useRefresh();
  const save = async () => {
    if (!content.trim()) return;
    await api.post("/interactions", { client_id: clientId, type, content });
    setContent("");
    toast.success("Registrato nello storico");
    refresh();
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 bg-slate-50/60">
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="sm:w-36 bg-white" data-testid="log-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>{INTERACTION_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
      <Textarea data-testid="log-content" rows={1} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Annota cosa vi siete detti…" className="bg-white min-h-[40px]" />
      <Button data-testid="log-save-btn" onClick={save} className="bg-[#0F172A] hover:bg-sky-600">Registra</Button>
    </div>
  );
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const { data: c } = useApi(`/clients/${id}`);
  const [edit, setEdit] = useState(false);
  const [msg, setMsg] = useState(false);
  const [rem, setRem] = useState(false);
  const [prac, setPrac] = useState(null);
  const [complete, setComplete] = useState(null);
  const [del, setDel] = useState(false);
  if (!c) return <div className="text-slate-400">Caricamento…</div>;

  const remove = async () => {
    try { await api.delete(`/clients/${id}`); toast.success("Nominativo eliminato"); refresh(); navigate("/clienti"); } catch (e) { toast.error(errMsg(e)); }
  };
  const openReminders = c.reminders.filter((r) => !r.done);
  const doneReminders = c.reminders.filter((r) => r.done);

  return (
    <div className="space-y-6 fade-up" data-testid="client-detail-page">
      <Link to="/clienti" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" />Clienti</Link>

      <div className="bg-white border border-slate-200/80 rounded-xl p-6 flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 data-testid="client-name" className="text-3xl font-extrabold tracking-tight text-slate-900">{c.first_name} {c.last_name}</h1>
            <span className="text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-700">{statusLabel(c.status)}</span>
            {c.privacy_consent && <ShieldCheck className="h-4 w-4 text-emerald-600" title="Privacy acquisita" />}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">{c.interests.map((i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full text-white" style={{ background: productOf(i).color }}>{productOf(i).label}</span>)}</div>
          <p className="text-sm text-slate-500 mt-3 font-mono">{c.phone} {c.phone && c.email && "·"} {c.email}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.phone && <a data-testid="client-call-btn" href={telLink(c.phone)} className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm hover:border-slate-900"><Phone className="h-4 w-4" />Chiama</a>}
          {c.phone && <a data-testid="client-wa-btn" href={waLink(c.phone, "")} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2 text-sm"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
          <button data-testid="client-message-btn" onClick={() => setMsg(true)} className="flex items-center gap-2 rounded-full bg-sky-600 text-white px-4 py-2 text-sm hover:bg-sky-700"><Send className="h-4 w-4" />Messaggio / Email</button>
          <button data-testid="client-reminder-btn" onClick={() => setRem(true)} className="flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-4 py-2 text-sm hover:bg-slate-700"><CalendarPlus className="h-4 w-4" />Richiamo</button>
          <button data-testid="client-edit-btn" onClick={() => setEdit(true)} className="h-9 w-9 grid place-items-center rounded-full border border-slate-200 hover:border-slate-500"><Pencil className="h-4 w-4" /></button>
          <button data-testid="client-export-btn" title="Esporta dati del cliente (GDPR)" onClick={() => downloadJson(`/clients/${id}/export`, `cliente_${c.last_name || id}.json`)} className="h-9 w-9 grid place-items-center rounded-full border border-slate-200 hover:border-slate-500"><Download className="h-4 w-4" /></button>
          <button data-testid="client-delete-btn" onClick={() => setDel(true)} className="h-9 w-9 grid place-items-center rounded-full border border-slate-200 text-rose-600 hover:border-rose-500"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      <AiSummary key={c.client_id} client={c} />
      <PrivacyCard client={c} />

      <Tabs defaultValue="storico">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger data-testid="tab-storico" value="storico">Storico contatti</TabsTrigger>
          <TabsTrigger data-testid="tab-richiami" value="richiami">Richiami ({openReminders.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-pratiche" value="pratiche">Pratiche ({c.practices.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-anagrafica" value="anagrafica">Anagrafica</TabsTrigger>
        </TabsList>

        <TabsContent value="storico" className="bg-white border border-slate-200/80 rounded-xl overflow-hidden">
          <LogForm clientId={id} />
          <div className="p-5" data-testid="timeline">
            {c.interactions.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Nessun contatto registrato.</p>}
            <ol className="relative border-l border-slate-200 ml-2 space-y-5">
              {c.interactions.map((i) => (
                <li key={i.interaction_id} className="pl-5 relative">
                  <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-sky-500" />
                  <p className="text-xs font-mono text-slate-400">{fmtDateTime(i.created_at)} · <span className="uppercase text-slate-600">{i.type}</span></p>
                  <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{i.content}</p>
                </li>
              ))}
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="richiami" className="bg-white border border-slate-200/80 rounded-xl overflow-hidden">
          {openReminders.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Nessun richiamo aperto.</p>}
          {openReminders.map((r) => <ReminderRow key={r.reminder_id} r={{ ...r, client_name: `${c.first_name} ${c.last_name}`, client_phone: c.phone, client_email: c.email }} onComplete={(x) => setComplete(x)} showDate />)}
          {doneReminders.length > 0 && (
            <div className="border-t border-slate-100 p-5">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-3">Completati</p>
              {doneReminders.map((r) => <p key={r.reminder_id} className="text-sm text-slate-500 py-1"><span className="font-mono text-xs">{fmtDateTime(r.due_at)}</span> · {r.reason || "Richiamo"} {r.outcome && `→ ${r.outcome}`}</p>)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pratiche" className="space-y-3">
          <button data-testid="client-add-practice-btn" onClick={() => setPrac({})} className="flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-4 py-2 text-sm hover:border-slate-900"><Plus className="h-4 w-4" />Nuova pratica</button>
          <div className="grid md:grid-cols-2 gap-4">
            {c.practices.map((p) => (
              <button key={p.practice_id} data-testid={`practice-card-${p.practice_id}`} onClick={() => setPrac(p)} className="text-left bg-white border border-slate-200/80 rounded-xl p-5 hover:shadow-md transition-shadow border-l-4" style={{ borderLeftColor: productOf(p.product).color }}>
                <div className="flex justify-between"><span className="font-display font-semibold">{productOf(p.product).label}</span><span className={`text-xs px-2 py-0.5 rounded-md border ${stageOf(p.stage).cls}`}>{stageOf(p.stage).label}</span></div>
                <p className="font-mono text-xl mt-2">{fmtEur(p.amount)}</p>
                <p className="text-xs text-slate-500 mt-1">{p.bank || "—"} {p.duration_months ? `· ${p.duration_months} mesi` : ""} {p.rate ? `· TAN ${p.rate}%` : ""}</p>
                {p.deadline && <p className="text-xs text-slate-500 mt-1">Scadenza: {fmtDate(p.deadline)}</p>}
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="anagrafica" className="bg-white border border-slate-200/80 rounded-xl p-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Info label="Codice fiscale" value={c.tax_code?.toUpperCase()} mono />
            <Info label="Data di nascita" value={c.birth_date && fmtDate(c.birth_date)} />
            <Info label="Città" value={c.city} />
            <Info label="Professione" value={c.job} />
            <Info label="Reddito netto mensile" value={c.income != null ? fmtEur(c.income) : ""} mono />
            <Info label="Fonte" value={c.source} />
            <Info label="Privacy" value={c.privacy_consent ? "Acquisita" : "Non acquisita"} />
            <Info label="Inserito il" value={fmtDate(c.created_at)} />
          </div>
          {c.notes && <div className="mt-6"><p className="text-xs text-slate-400 uppercase tracking-wider">Note</p><p className="text-sm mt-1 whitespace-pre-wrap">{c.notes}</p></div>}
        </TabsContent>
      </Tabs>

      <ClientFormDialog open={edit} onOpenChange={setEdit} client={c} />
      <MessageDialog open={msg} onOpenChange={setMsg} client={c} />
      <ReminderDialog open={rem} onOpenChange={setRem} clientId={id} />
      <PracticeFormDialog open={!!prac} onOpenChange={() => setPrac(null)} practice={prac?.practice_id ? prac : null} clientId={id} />
      <CompleteReminderDialog reminder={complete} onOpenChange={() => setComplete(null)} />
      <AlertDialog open={del} onOpenChange={setDel}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Eliminare {c.first_name} {c.last_name}?</AlertDialogTitle><AlertDialogDescription>Verranno eliminati definitivamente anche pratiche, richiami, storico contatti, documenti caricati, firme privacy e chat di prenotazione. Se ti serve, esporta prima i dati.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-client" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Elimina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
